import { COUPON_CODE_RE, normalizeCode } from "./coupons";
import { isPaymentProvider } from "./payments";
import { isShippingZone } from "./shipping";
import type { Locale, OrderInput } from "./types";

export const MAX_ITEMS_PER_ORDER = 30;
export const MAX_QTY_PER_ITEM = 20;

export interface ValidationResult<T> {
  ok: boolean;
  value?: T;
  errors: Record<string, string>;
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+\d][\d\s()-]{6,24}$/;

function str(value: unknown, max: number): string {
  return typeof value === "string" ? value.trim().slice(0, max) : "";
}

/**
 * Validates an untrusted checkout payload. Error values are i18n keys so the
 * client can show localized messages next to each field.
 */
export function validateOrderInput(raw: unknown): ValidationResult<OrderInput> {
  const errors: Record<string, string> = {};
  if (typeof raw !== "object" || raw === null) {
    return { ok: false, errors: { form: "checkout.errors.invalid" } };
  }
  const input = raw as Record<string, unknown>;
  const c = (typeof input.customer === "object" && input.customer !== null ? input.customer : {}) as Record<string, unknown>;

  const customer = {
    name: str(c.name, 80),
    email: str(c.email, 120).toLowerCase(),
    phone: str(c.phone, 30),
    city: str(c.city, 80),
    address: str(c.address, 200),
    zone: isShippingZone(c.zone) ? c.zone : "",
    notes: str(c.notes, 500),
  };
  if (customer.name.length < 2) errors.name = "checkout.errors.name";
  if (!EMAIL_RE.test(customer.email)) errors.email = "checkout.errors.email";
  if (!PHONE_RE.test(customer.phone)) errors.phone = "checkout.errors.phone";
  if (customer.city.length < 2) errors.city = "checkout.errors.city";

  // Home delivery, or pickup at a point whose address is sent on WhatsApp (the server checks it is on).
  const delivery = input.delivery === "shipping" || input.delivery === "pickup" ? input.delivery : null;
  if (!delivery) errors.delivery = "checkout.errors.delivery";
  if (delivery === "shipping") {
    if (customer.address.length < 5) errors.address = "checkout.errors.address";
    if (!customer.zone) errors.zone = "checkout.errors.zone";
  } else {
    customer.address = "";
    customer.zone = "";
  }

  const locale: Locale = input.locale === "en" ? "en" : "es";
  const couponCode = normalizeCode(input.couponCode);
  if (couponCode && !COUPON_CODE_RE.test(couponCode)) errors.coupon = "checkout.coupon.invalid";

  const paymentProvider = isPaymentProvider(input.paymentProvider) ? input.paymentProvider : null;
  if (!paymentProvider) errors.paymentProvider = "checkout.errors.payment";

  const merged = new Map<number, number>();
  if (!Array.isArray(input.items) || input.items.length === 0) {
    errors.items = "checkout.errors.empty";
  } else if (input.items.length > MAX_ITEMS_PER_ORDER) {
    errors.items = "checkout.errors.tooMany";
  } else {
    for (const item of input.items as unknown[]) {
      const it = (item ?? {}) as Record<string, unknown>;
      const id = Number(it.variantId);
      const qty = Number(it.quantity);
      if (!Number.isInteger(id) || id <= 0 || !Number.isInteger(qty) || qty <= 0 || qty > MAX_QTY_PER_ITEM) {
        errors.items = "checkout.errors.items";
        break;
      }
      merged.set(id, Math.min((merged.get(id) ?? 0) + qty, MAX_QTY_PER_ITEM));
    }
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return {
    ok: true,
    errors: {},
    value: {
      customer,
      delivery: delivery!,
      locale,
      paymentProvider: paymentProvider!,
      ...(couponCode ? { couponCode } : {}),
      items: [...merged].map(([variantId, quantity]) => ({ variantId, quantity })),
    },
  };
}
